import React, { useEffect, useState, useRef } from 'react';
import { 
  Button, Box, Container, Paper, Typography, CircularProgress, Backdrop,
  TextField, InputAdornment, Tooltip, IconButton, Stack, Chip, Card, CardContent, Grid,
  Menu, MenuItem, ListItemIcon, ListItemText, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Select, FormControl, InputLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import InvoicePreviewDialog from '../../../components/InvoicePreviewDialog';
import * as saleOrderService from '../services/saleOrderService';
import { generateSaleOrderPdfHtml } from '../services/saleOrderPdfService';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import { usePaginationWithUrl } from '../../../hooks/usePaginationWithUrl';
import html2pdf from 'html2pdf.js';
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import { PAYMENT_STATUS } from '../../../constants/paymentStatus';

// Icons
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Components
import SaleOrderStatusChip from '../components/SaleOrderStatusChip';
import PaymentStatusChip from '../components/PaymentStatusChip';

const _now = new Date();
const _currentYear = _now.getFullYear();
const _currentQuarter = Math.ceil((_now.getMonth() + 1) / 3);
const QUICK_DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'currentMonth', label: 'Current Month' },
  { value: 'lastMonth', label: 'Last Month' },
  ...Array.from({ length: _currentQuarter }, (_, i) => ({
    value: `q${i + 1}_${_currentYear}`,
    label: `Q${i + 1}`,
  })),
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Closed', label: 'Closed' },
];

// Uses local date parts to avoid UTC offset date-shifting
const localDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDateRange = (filter) => {
  const today = new Date();
  const todayStr = localDate(today);
  if (filter === 'today') return { startDate: todayStr, endDate: todayStr };
  if (filter === 'yesterday') {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    const yStr = localDate(y);
    return { startDate: yStr, endDate: yStr };
  }
  if (filter === 'currentMonth') {
    return { startDate: localDate(new Date(today.getFullYear(), today.getMonth(), 1)), endDate: todayStr };
  }
  if (filter === 'lastMonth') {
    const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    return { startDate: localDate(firstOfLastMonth), endDate: localDate(lastOfLastMonth) };
  }
  const qMatch = filter.match(/^q(\d)_(\d{4})$/);
  if (qMatch) {
    const q = parseInt(qMatch[1]);
    const year = parseInt(qMatch[2]);
    const qStartMonth = (q - 1) * 3;
    const qStart = new Date(year, qStartMonth, 1);
    const qNaturalEnd = new Date(year, qStartMonth + 3, 0); // last day of quarter
    const endOfQ = qNaturalEnd > today ? today : qNaturalEnd;
    return { startDate: localDate(qStart), endDate: localDate(endOfQ) };
  }
  return { startDate: '', endDate: '' };
};

const ListSaleOrderPage = () => {
  const [saleOrders, setSaleOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewedSaleOrder, setPreviewedSaleOrder] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSaleOrder, setSelectedSaleOrder] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSaleOrder, setMenuSaleOrder] = useState(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateQuickFilter, setDateQuickFilter] = useState('currentMonth');
  const [startDate, setStartDate] = useState(() => getDateRange('currentMonth').startDate);
  const [endDate, setEndDate] = useState(() => getDateRange('currentMonth').endDate);
  const searchDebounceRef = useRef(null);
  const { t, i18n } = useTranslation();
  const { storeInfo } = useStoreInfo();
  const reduxLanguage = useSelector((state) => state.settings?.language);
  const language = reduxLanguage || i18n.language || 'en';

  const handleCopy = (e, text) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text || '');
    toast.success(t('copied') || 'Copied!');
  };
  
  const { 
    page, 
    pageSize, 
    pageSizeOptions,
    handlePageChange, 
    handlePageSizeChange 
  } = usePaginationWithUrl({ defaultPageSize: 10, pageSizeOptions: [10, 20, 50, 100] });
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchSaleOrders();
  // eslint-disable-next-line
  }, [page, pageSize, statusFilter, startDate, endDate]);

  const fetchSaleOrders = async (
    overrides = {}
  ) => {
    setLoading(true);
    try {
      const params = {
        pageNumber: page + 1,
        pageSize: pageSize,
        searchTerm: overrides.searchTerm ?? (searchQuery || undefined),
        status: overrides.status ?? (statusFilter !== 'all' ? statusFilter : undefined),
        startDate: overrides.startDate ?? (startDate || undefined),
        endDate: overrides.endDate ?? (endDate || undefined),
        sortBy: 'CreatedDate',
        sortOrder: 'desc',
      };
      
      const response = await saleOrderService.getSaleOrders(params);
      // Handle paginated response structure
      const data = response?.payload || response?.data || response;
      
      const saleOrdersArray = data?.Items || data?.items || data || [];
      const total = data?.TotalItems || data?.TotalCount || data?.totalItems || data?.totalCount || saleOrdersArray.length;
      
      setSaleOrders(Array.isArray(saleOrdersArray) ? saleOrdersArray : []);
      setTotalCount(total);
    } catch (error) {

      toast.error(t('failed_to_load_sale_orders'));
      setSaleOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchSaleOrders();
      toast.success(t('sale_orders_refreshed'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleQuickDateFilter = (value) => {
    setDateQuickFilter(value);
    const range = getDateRange(value);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => fetchSaleOrders({ searchTerm: value || undefined }), 1500);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDateQuickFilter('');
    setStartDate('');
    setEndDate('');
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  };

  const handleDeleteClick = (saleOrder) => {
    setSelectedSaleOrder(saleOrder);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleMenuOpen = (event, saleOrder) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuSaleOrder(saleOrder);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSaleOrder(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSaleOrder) return;
    
    try {
      await saleOrderService.deleteSaleOrder(selectedSaleOrder.Id);
      toast.success(t('sale_order_deleted'));
      fetchSaleOrders();
    } catch (error) {

      toast.error(t('failed_to_delete_sale_order'));
    } finally {
      setDeleteDialogOpen(false);
      setSelectedSaleOrder(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const handlePreviewPdf = async (saleOrder) => {
    setPreviewLoading(true);
    try {
      // Check if storeInfo is loaded
      if (!storeInfo) {
        toast.error(t('store_info_not_loaded') || 'Store information not loaded');
        setPreviewLoading(false);
        return;
      }

      const fullSO = await saleOrderService.getSaleOrderById(saleOrder.Id);
      const data = fullSO?.payload || fullSO?.data || fullSO || saleOrder;
      
      console.log('Full sale order data:', data);
      console.log('LineItems:', data.LineItems);
      console.log('LineItems with SKU check:', data.LineItems?.map(item => ({
        Name: item.Name || item.ProductName,
        SKUBarCode: item.SKUBarCode,
        SKU: item.SKU,
        BarCode: item.BarCode,
        Quantity: item.Quantity
      })));
      console.log('Store info:', storeInfo);
      console.log('Language:', language);
      
      // Construct properly formatted sale order data
      const saleOrderData = {
        SaleOrderNumber: data.SaleOrderNumber,
        SaleOrderDate: data.SaleOrderDate || data.CreatedDate,
        Customer: data.Customer,
        LineItems: data.LineItems || [],
        CustomText: data.CustomText || '',
        VATPercentage: data.VATPercentage || 21,
        Status: data.Status,
        PaymentStatus: data.PaymentStatus,
        PaidAmount: data.PaidAmount || 0,
        TotalIncl: data.TotalIncl,
        DiscountAmount: data.DiscountAmount || 0,
        IsTvaIncluded: data.IsTvaIncluded,
        PaymentMethod: data.PaymentMethod || '',
        PaymentMethods: data.PaymentMethods || [],
        OutstandingInvoices: data.OutstandingInvoices || [],
      };
      
      console.log('Formatted sale order data:', saleOrderData);
      
      const htmlContent = generateSaleOrderPdfHtml(saleOrderData, storeInfo, language);
      
      console.log('Generated HTML length:', htmlContent?.length);
      console.log('HTML preview:', htmlContent?.substring(0, 500));
      
      setPreviewHtml(htmlContent);
      setPreviewedSaleOrder(data);
      
      // Wait for state to update before opening dialog
      setTimeout(() => {
        setOpenPreview(true);
        setPreviewLoading(false);
      }, 100);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(t('failed_to_generate_pdf'));
      setPreviewLoading(false);
    }
  };

  // Stats calculation - current page only
  const stats = {
    total: totalCount,
    draftOnPage: saleOrders.filter(so => so.Status?.toLowerCase() === 'draft').length,
    closedOnPage: saleOrders.filter(so => so.Status?.toLowerCase() === 'closed').length,
    totalValueOnPage: saleOrders.reduce((sum, so) => sum + (Number(so.TotalIncl) || 0), 0),
  };

  const isDraft = menuSaleOrder?.Status?.toLowerCase() === 'draft';
  const isClosed = menuSaleOrder?.Status?.toLowerCase() === 'closed';
  const isAlreadyConverted = Boolean(menuSaleOrder?.InvoiceId || menuSaleOrder?.InvoiceNumber);
  const canConvertToInvoice = isClosed && !isAlreadyConverted;

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || startDate !== '' || endDate !== '';
  
  // Get current page data
  const displayedSaleOrders = saleOrders;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          navigate(`/sales/sale-orders/${menuSaleOrder?.Id}`);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('view_details')}</ListItemText>
        </MenuItem>

        {isDraft && (
          <MenuItem onClick={() => {
            navigate(`/sales/sale-orders/add?id=${menuSaleOrder?.Id}`);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <PlayArrowIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('continue')}</ListItemText>
          </MenuItem>
        )}

        {canConvertToInvoice && (
          <MenuItem onClick={() => {
            navigate(`/sales/sale-orders/convert?id=${menuSaleOrder?.Id}`);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>{t('convert_to_invoice')}</ListItemText>
          </MenuItem>
        )}

        {isAlreadyConverted && (
          <MenuItem onClick={() => {
            navigate(`/invoices/view/${menuSaleOrder?.InvoiceId}`);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" sx={{ color: '#9c27b0' }} />
            </ListItemIcon>
            <ListItemText>{t('view_invoice')}: {menuSaleOrder?.InvoiceNumber}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => {
          menuSaleOrder && handlePreviewPdf(menuSaleOrder);
          handleMenuClose();
        }}
        disabled={!isClosed}
        >
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('preview_pdf')}</ListItemText>
        </MenuItem>

        {isDraft && (
          <MenuItem onClick={() => {
            menuSaleOrder && handleDeleteClick(menuSaleOrder);
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>{t('delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShoppingCartIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {t('sale_orders')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {t('manage_sale_orders')}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/sales/sale-orders/add')}
            sx={{
              bgcolor: 'white',
              color: '#667eea',
              fontWeight: 700,
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
          >
            {t('create_sale_order')}
          </Button>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('total_sale_orders')}
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('draft_orders')} ({t('current_page')})
              </Typography>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {stats.draftOnPage}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('closed_orders')} ({t('current_page')})
              </Typography>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {stats.closedOnPage}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('total_value')} ({t('current_page')})
              </Typography>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {formatCurrency(stats.totalValueOnPage)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        {/* Row 1: date quick chips (left) — status chips (right) */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {QUICK_DATE_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                clickable
                onClick={() => handleQuickDateFilter(opt.value)}
                color={dateQuickFilter === opt.value ? 'primary' : 'default'}
                variant={dateQuickFilter === opt.value ? 'filled' : 'outlined'}
                sx={{ fontWeight: dateQuickFilter === opt.value ? 600 : 400 }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {STATUS_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                clickable
                onClick={() => setStatusFilter(opt.value)}
                color={statusFilter === opt.value ? 'secondary' : 'default'}
                variant={statusFilter === opt.value ? 'filled' : 'outlined'}
                sx={{ fontWeight: statusFilter === opt.value ? 600 : 400 }}
              />
            ))}
          </Box>
        </Box>

        {/* Row 2: filter fields */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Three equal-width fields filling the available space */}
          <Box sx={{ flex: 1, display: 'flex', gap: 1, minWidth: 0 }}>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                label={t('search') || 'Search'}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('search_sale_orders_placeholder') || 'Order #, customer, TVA...'}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} /></InputAdornment>,
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => handleSearchChange('')} sx={{ p: 0.25 }}><ClearIcon fontSize="small" /></IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('start_date') || 'Start Date'}
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setDateQuickFilter(''); }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('end_date') || 'End Date'}
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setDateQuickFilter(''); }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flex: '0 0 auto' }}>
            <Tooltip title={t('refresh') || 'Refresh'}>
              <IconButton onClick={handleRefresh} disabled={refreshing} color="primary" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {hasActiveFilters && (
              <Tooltip title={t('clear_filters') || 'Clear Filters'}>
                <IconButton onClick={handleClearFilters} color="secondary" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Row 3: active filter chips */}
        {hasActiveFilters && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
            <FilterListIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            {searchQuery && (
              <Chip
                label={`${t('search') || 'Search'}: "${searchQuery}"`}
                size="small"
                onDelete={() => handleSearchChange('')}
                sx={{ fontWeight: 500 }}
              />
            )}
            {statusFilter !== 'all' && (
              <Chip
                label={statusFilter}
                size="small"
                onDelete={() => setStatusFilter('all')}
                sx={{ fontWeight: 500 }}
              />
            )}
            {startDate && (
              <Chip
                label={`${t('from') || 'From'}: ${startDate}`}
                size="small"
                onDelete={() => { setStartDate(''); setDateQuickFilter(''); }}
                sx={{ fontWeight: 500 }}
              />
            )}
            {endDate && (
              <Chip
                label={`${t('to') || 'To'}: ${endDate}`}
                size="small"
                onDelete={() => { setEndDate(''); setDateQuickFilter(''); }}
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Data Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('sale_order_number')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('customer')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('date')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('items')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('paid_amount')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('payment_status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('total')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea', textAlign: 'center' }}>
                      {t('actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedSaleOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          {t('no_sale_orders_found')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedSaleOrders.map((so) => (
                      <TableRow 
                        key={so.Id}
                        sx={{
                          '&:hover': { bgcolor: '#f8f9fa', cursor: 'pointer' },
                          borderBottom: '1px solid #e0e0e0'
                        }}
                        onClick={() => navigate(`/sales/sale-orders/${so.Id}`)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ShoppingCartIcon fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight={600}>
                              {so.SaleOrderNumber}
                            </Typography>
                            <Tooltip title={t('copy') || 'Copy'}>
                              <IconButton size="small" onClick={(e) => handleCopy(e, so.SaleOrderNumber)} sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                                <ContentCopyIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {so.Customer?.CompanyName || so.Customer?.Name || so.CustomerName || t('unknown_customer')}
                              </Typography>
                              <Tooltip title={t('copy') || 'Copy'}>
                                <IconButton size="small" onClick={(e) => handleCopy(e, so.Customer?.CompanyName || so.Customer?.Name || so.CustomerName || '')} sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                                  <ContentCopyIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {(so.Customer?.VATNumber || so.CustomerVATNumber) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {so.Customer?.VATNumber || so.CustomerVATNumber}
                                </Typography>
                                <Tooltip title={t('copy') || 'Copy'}>
                                  <IconButton size="small" onClick={(e) => handleCopy(e, so.Customer?.VATNumber || so.CustomerVATNumber || '')} sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                                    <ContentCopyIcon sx={{ fontSize: 11 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {formatDate(so.CreatedDate)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(so.CreatedDate).toLocaleTimeString(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {so.LineItemsCount || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ color: 'success.main' }}>
                            {formatCurrency(so.PaidAmount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <PaymentStatusChip paymentStatus={so.PaymentStatus || PAYMENT_STATUS.NOT_PAID} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(so.TotalIncl)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <SaleOrderStatusChip status={so.Status} />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, so);
                            }}
                            sx={{ color: '#666' }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={pageSizeOptions}
              showFirstButton
              showLastButton
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '.MuiTablePagination-actions': {
                  display: 'flex'
                }
              }}
            />
          </>
        )}
      </Paper>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={previewLoading || refreshing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('confirm_delete')}
        message={t('confirm_delete_sale_order_message', { number: selectedSaleOrder?.SaleOrderNumber })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedSaleOrder(null);
        }}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        variant="danger"
      />

      {/* PDF Preview Dialog */}
      <InvoicePreviewDialog
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setPreviewedSaleOrder(null);
        }}
        html={previewHtml}
        documentTitle={`Sale_Order_${previewedSaleOrder?.SaleOrderNumber || 'Preview'}`}
      />
    </Container>
  );
};

export default ListSaleOrderPage;
